import { useCallback, useEffect, useState } from "react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { useMe } from "../../context/MeContext.js";
import { HR_ROLES } from "@hrms/types";
import { Button } from "../../components/ui/button.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { DateInput } from "../../components/ui/date-input.js";
import { Combobox } from "../../components/ui/combobox.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import { EmptyState, Field, fmtDate, toEmployeeOptions } from "./shared.js";
import type { TemporaryManagerDelegation } from "./types.js";

// The "Temporary Support (grant / revoke / history)" control the spec places on
// the review list's team rows (sections 3 & 13). It fetches the review's
// delegations, then shows either the assign form (no active delegate) or the
// active delegate with a Revoke button, plus the full history table. The server
// enforces who may grant/revoke; the list only offers this on team rows.

interface CandidateOption {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
}

// The history table, shared with the detail-page read panel's "View" dialog.
export function DelegationHistoryTable({ rows }: { rows: TemporaryManagerDelegation[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-surface-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-3">Temporary manager</TableHead>
            <TableHead>Window</TableHead>
            <TableHead>Granted by</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="pl-3 font-medium">{r.delegateName}</TableCell>
              <TableCell className="whitespace-nowrap text-surface-500">
                {fmtDate(r.startDate)} – {r.endDate ? fmtDate(r.endDate) : "Open-ended"}
              </TableCell>
              <TableCell>{r.grantedByName}</TableCell>
              <TableCell>
                {r.revokedAt ? (
                  <Badge tone="error">Revoked {fmtDate(r.revokedAt)}</Badge>
                ) : r.isActive ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <Badge tone="neutral">Ended</Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function TemporaryManagerModal({
  open,
  reviewId,
  employeeId,
  employeeName,
  fiscalYear,
  onClose,
  onChanged,
}: {
  open: boolean;
  reviewId: string | null;
  employeeId: string | null;
  employeeName: string | null;
  // The review's fiscal year — bounds the access window to that year (the
  // server enforces the same, against the review's real period span).
  fiscalYear: number | null;
  onClose: () => void;
  // Called after a grant/revoke so the list can refresh if it ever shows status.
  onChanged?: () => void;
}) {
  const toast = useToast();
  const me = useMe();
  const [rows, setRows] = useState<TemporaryManagerDelegation[]>([]);
  const [candidates, setCandidates] = useState<CandidateOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [delegateId, setDelegateId] = useState("");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  // Editable copy of the active delegation's access window.
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [error, setError] = useState("");

  const active = rows.find((r) => r.isActive) ?? null;

  // HR/superuser may revoke at any time (e.g. undo a wrong assignment). Anyone
  // else (the line manager) must wait until the temporary manager submits their
  // report. HR_ROLES already includes the superuser.
  const viewerIsHrOrSuper = HR_ROLES.has(me?.role ?? "");
  const canRevokeNow = viewerIsHrOrSuper || active?.commentSubmittedAt != null;

  // The access window is bounded to the review's fiscal year (Jan–Dec of it);
  // the server enforces the same against the review's real period span.
  const fyMin = fiscalYear != null ? `${fiscalYear}-01-01` : undefined;
  const fyMax = fiscalYear != null ? `${fiscalYear}-12-31` : undefined;

  // Seed the date editors from the active delegation whenever it changes.
  useEffect(() => {
    setEditStart(active ? active.startDate.slice(0, 10) : "");
    setEditEnd(active?.endDate ? active.endDate.slice(0, 10) : "");
  }, [active?.id, active?.startDate, active?.endDate]);

  const load = useCallback(async () => {
    if (!reviewId) return;
    setLoaded(false);
    setError("");
    try {
      // Eligible delegates are managers/upper-managers/HR only (keyed on login
      // role server-side) — never plain engineers. Exclude the reviewed employee
      // and the current user, who cannot be their own/the delegate.
      const [dels, emps] = await Promise.all([
        api.get<TemporaryManagerDelegation[]>(`/performance/reviews/${reviewId}/delegations`),
        api.get<CandidateOption[]>("/performance/eligible-delegates"),
      ]);
      setRows(dels.data);
      setCandidates(emps.data.filter((e) => e.id !== employeeId && e.id !== me?.employeeId));
    } catch (e) {
      logError("TemporaryManagerModal.load", e);
    } finally {
      setLoaded(true);
    }
  }, [reviewId, employeeId, me?.employeeId]);

  useEffect(() => {
    if (!open) return;
    // Default to today, but clamp into the review's fiscal year so the default
    // is never outside the allowed window (e.g. for a previous-year review).
    const today = new Date().toISOString().slice(0, 10);
    const clamped = fyMin && today < fyMin ? fyMin : fyMax && today > fyMax ? fyMax : today;
    setDelegateId("");
    setStartDate(clamped);
    setEndDate("");
    void load();
  }, [open, load, fyMin, fyMax]);

  async function assign() {
    if (!reviewId) return;
    if (!delegateId) {
      setError("Pick a manager");
      throw new Error("validation");
    }
    setError("");
    try {
      await api.post(`/performance/reviews/${reviewId}/delegations`, {
        delegateId,
        startDate,
        endDate: endDate || null,
      });
      toast.show("Temporary manager assigned", "success");
      onChanged?.();
      await load();
    } catch (e) {
      logError("TemporaryManagerModal.assign", e);
      setError(errMsg(e) ?? "Could not assign the temporary manager");
      throw e;
    }
  }

  async function saveDates() {
    if (!active) return;
    if (!editStart) {
      setError("Pick a start date");
      throw new Error("validation");
    }
    if (editEnd && editEnd < editStart) {
      setError("End date cannot be before the start date");
      throw new Error("validation");
    }
    setError("");
    try {
      await api.put(`/performance/delegations/${active.id}/dates`, {
        startDate: editStart,
        endDate: editEnd || null,
      });
      toast.show("Access dates updated", "success");
      onChanged?.();
      await load();
    } catch (e) {
      logError("TemporaryManagerModal.saveDates", e);
      setError(errMsg(e) ?? "Could not update the access dates");
      throw e;
    }
  }

  async function revoke() {
    if (!active) return;
    try {
      await api.post(`/performance/delegations/${active.id}/revoke`, {});
      toast.show("Temporary manager revoked", "success");
      onChanged?.();
      await load();
    } catch (e) {
      logError("TemporaryManagerModal.revoke", e);
      toast.show(errMsg(e) ?? "Could not revoke the temporary manager", "error");
      throw e;
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Temporary manager{employeeName ? ` — ${employeeName}` : ""}</DialogTitle>
        </DialogHeader>

        {!loaded ? (
          <p className="py-6 text-center text-[13px] text-surface-400">Loading…</p>
        ) : (
          <div className="grid gap-5">
            {active ? (
              <div className="grid gap-4 rounded-md border border-surface-200 p-3">
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  <Badge tone="info">{active.delegateName}</Badge>
                  <span className="text-surface-400">Granted by {active.grantedByName}</span>
                  {active.commentSubmittedAt ? (
                    <Badge tone="success">Report submitted</Badge>
                  ) : (
                    <Badge tone="warning">Report pending</Badge>
                  )}
                  <AsyncButton
                    className="ml-auto"
                    variant="destructive"
                    size="sm"
                    disabled={!canRevokeNow}
                    onClick={() => revoke()}
                    confirm={{
                      title: "Revoke the temporary manager?",
                      description: `${active.delegateName} will lose access to this review immediately.`,
                      actionText: "Revoke",
                      tone: "destructive",
                    }}
                  >
                    Revoke
                  </AsyncButton>
                </div>
                {!canRevokeNow && (
                  <p className="text-[12px] text-surface-400">
                    You can remove access once {active.delegateName} submits their report. They are notified to submit it.
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Start date">
                    <DateInput isUS value={editStart} onChange={setEditStart} min={fyMin} max={fyMax} />
                  </Field>
                  <Field label="End date (optional)" hint="Leave blank for open-ended.">
                    <DateInput isUS value={editEnd} onChange={setEditEnd} min={editStart || fyMin} max={fyMax} />
                  </Field>
                </div>
                {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
                <div className="flex justify-end">
                  <AsyncButton
                    size="sm"
                    disabled={
                      !editStart ||
                      (editStart === active.startDate.slice(0, 10) &&
                        editEnd === (active.endDate?.slice(0, 10) ?? ""))
                    }
                    onClick={() => saveDates()}
                  >
                    Save dates
                  </AsyncButton>
                </div>
              </div>
            ) : (
              <div className="grid gap-4">
                <Field label="Temporary manager">
                  <Combobox
                    value={delegateId}
                    onValueChange={setDelegateId}
                    options={toEmployeeOptions(candidates)}
                    placeholder="Select a manager"
                    searchPlaceholder="Search by name or employee ID"
                    emptyText="No one matches."
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Start date">
                    <DateInput isUS value={startDate} onChange={setStartDate} min={fyMin} max={fyMax} />
                  </Field>
                  <Field label="End date (optional)" hint="Leave blank for open-ended.">
                    <DateInput isUS value={endDate} onChange={setEndDate} min={startDate || fyMin} max={fyMax} />
                  </Field>
                </div>
                {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
                <div className="flex justify-end">
                  <AsyncButton disabled={!delegateId || !startDate} onClick={() => assign()}>
                    Assign
                  </AsyncButton>
                </div>
              </div>
            )}

            <div>
              <p className="mb-2 text-[12px] font-semibold tracking-wide text-surface-500 uppercase">History</p>
              {rows.length === 0 ? (
                <EmptyState message="No temporary manager has been assigned on this review." />
              ) : (
                <DelegationHistoryTable rows={rows} />
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
